import { Loader } from "@trackman/web-shared-components";
import Skeleton from "@trackman/web-shared-components/components/LoadingPlaceholder/Skeleton";
import { AppButton, AppInput, AppRadioGroup, AppTextarea } from "components/form";
import { AppSpacer } from "components/ui";
import { t } from "i18next";
import { useStores } from "index";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { IUserDiagnostic, UserDiagnosticsFileType, UserDiagnosticsState } from "types/models/UserDiagnosticsModel";
import { DevopsLinkShouldBeValid } from "utilities/Validations";
import UserDiagnosticsFileIndicator from "../UserDiagnosticsFileIndicator";

interface IUserDignosticsDevopsTabProps {
    userDiagnostic: IUserDiagnostic | null;
    isHandlingTicket: boolean;
}

export const UserDignosticsDevopsIntegrationTab = observer(({ userDiagnostic, isHandlingTicket }: IUserDignosticsDevopsTabProps) => {
    const [isCreateContentActive, setIsCreateContentActive] = useState<boolean>(true);

    const createTicketTitle = t("UserDiagnostics.Tab.devops_integration.RadioButton.Create");
    const linkTicketTitle = t("UserDiagnostics.Tab.devops_integration.RadioButton.Link");
    const currentContentTitle = isCreateContentActive ? createTicketTitle : linkTicketTitle;

    const radioGroupOptions = [
        {
            label: createTicketTitle,
            value: "0"
        },
        {
            label: linkTicketTitle,
            value: "1"
        }
    ]

    useEffect(() => {
        const isCreateEnabled = userDiagnostic?.azureDevopsLink === null;

        setIsCreateContentActive(isCreateEnabled)
    }, [userDiagnostic]);

    const handleSelectingRadioGroup = useCallback((value: string) => {
        const isCreateSelected = value === "0";
        setIsCreateContentActive(isCreateSelected)
    }, []);

    if (!userDiagnostic) {
        return <div className='container'>
            <Skeleton variant='rectangular' height={200} />
        </div>
    }

    return (
        <>
            <AppRadioGroup value={isCreateContentActive ? "0" : "1"} options={radioGroupOptions} onChange={handleSelectingRadioGroup} isHorizontal={true} width='auto' />
            <div className='card p-2'>
                <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23 text-underline'>
                    <span className='mr-2'>{currentContentTitle}</span>
                </h3>
                {isCreateContentActive ?
                    (
                        <UserDignosticsCreateTicketSection userDiagnostic={userDiagnostic} isHandlingTicket={isHandlingTicket} />
                    )
                    :
                    (
                        <UserDignosticsLinkTicketSection userDiagnostic={userDiagnostic} isHandlingTicket={isHandlingTicket} />
                    )
                }
            </div>
        </>
    );
})

interface IUserDignosticsCreateTicketSectionProps {
    userDiagnostic: IUserDiagnostic;
    isHandlingTicket: boolean;
}

export const UserDignosticsCreateTicketSection = observer(({ userDiagnostic, isHandlingTicket }: IUserDignosticsCreateTicketSectionProps) => {
    const { userDiagnosticStore } = useStores();

    const [isCreateTicketContentEnabled, setIsCreateTicketContentEnabled] = useState<boolean>(true);
    const [subjectValue, setSubjectValue] = useState<string>(userDiagnostic?.userSubject ?? '');
    const [descriptionValue, setDescriptionValue] = useState<string>(userDiagnostic?.userDescription ?? '');

    useEffect(() => {
        const isCreateEnabled = userDiagnostic.azureDevopsLink === null;

        setIsCreateTicketContentEnabled(isCreateEnabled)
        setSubjectValue(userDiagnostic?.userSubject ?? '')
        setDescriptionValue(userDiagnostic?.userDescription ?? '')
    }, [userDiagnostic]);

    const handleSubjectChanged = useCallback(
        (value: string) => {
            setSubjectValue(value)
        },
        [userDiagnostic]
    );

    const handleDescriptionChanged = useCallback(
        (value: string) => {
            setDescriptionValue(value)
        },
        [userDiagnostic]
    );

    const isCreateTicketButtonDisabled = useMemo(() => {
        return userDiagnostic.state === UserDiagnosticsState.Closed || isHandlingTicket || !isCreateTicketContentEnabled;
    }, [userDiagnostic, isHandlingTicket, isCreateTicketContentEnabled]);

    return (
        <section className='d-flex align-items-center justify-content-center 4rem'>
            <section className='container'>
                <div className="row">
                    <div className="col-11 p-3">
                        <AppInput
                            label={t('UserDiagnostics.Column.Subject')}
                            name={'subject'}
                            value={subjectValue ?? ''}
                            disabled={isCreateTicketButtonDisabled}
                            onChange={(e) => handleSubjectChanged(e.target.value)}
                        />
                        <AppSpacer space='15px' />
                        <AppTextarea
                            label={t('UserDiagnostics.Column.Description')}
                            name={'description'}
                            value={descriptionValue ?? ''}
                            disabled={isCreateTicketButtonDisabled}
                            onChange={(e) => handleDescriptionChanged(e.target.value)}
                        />
                    </div>
                    <div className="col-1 p-3 justify-content-between">
                        <h4 className='font-13 font-700 font-lato text-capitalize mr-2 line-height-23 text-underline'>
                            <span className='mr-2'>{t('UserDiagnostics.Tab.devops_integration.Logs').toString()}</span>
                        </h4>
                        <UserDiagnosticsFileIndicator iconName="ImageFileIcon" containsFileType={(userDiagnostic.fileEntries ?? []).some(x => x.fileType === UserDiagnosticsFileType.AuxillaryFile)} />
                        <UserDiagnosticsFileIndicator iconName="FileIcon" containsFileType={(userDiagnostic.fileEntries ?? []).some(x => x.fileType === UserDiagnosticsFileType.IpadLog)} />
                        <UserDiagnosticsFileIndicator iconName="RadarIcon" containsFileType={(userDiagnostic.fileEntries ?? []).some(x => x.fileType === UserDiagnosticsFileType.RadarLog)} />
                        <UserDiagnosticsFileIndicator iconName="CameraIcon" containsFileType={(userDiagnostic.fileEntries ?? []).some(x => x.fileType === UserDiagnosticsFileType.CameraLog)} />
                    </div>
                </div>

                <AppSpacer space='15px' />
                <div className='d-flex align-items-end' style={{ gap: '1rem' }}>
                    {isHandlingTicket && <Loader size='small' />}
                    <AppButton
                        variant='primary'
                        onClick={() => userDiagnosticStore.createTicket(
                            {
                                userDiagnosticId: userDiagnostic.id,
                                devopsTicketOptions: {
                                    customSubject: subjectValue,
                                    customDescription: descriptionValue
                                }
                            })}
                        disabled={isCreateTicketButtonDisabled}
                        className='h-100 text-nowrap'
                    >
                        {t('UserDiagnostics.Tab.devops_integration.Button.Create').toString()}
                    </AppButton>
                </div>
            </section>
        </section>
    );
})

interface IUserDignosticsLinkTicketSectionProps {
    userDiagnostic: IUserDiagnostic;
    isHandlingTicket: boolean;
}

export const UserDignosticsLinkTicketSection = observer(({ userDiagnostic, isHandlingTicket }: IUserDignosticsLinkTicketSectionProps) => {
    const { userDiagnosticStore } = useStores();

    const [currentAzureDevopsLink, setCurrentAzureDevopsLink] = useState<string>(userDiagnostic?.azureDevopsLink ?? '');

    useEffect(() => {
        setCurrentAzureDevopsLink(userDiagnostic?.azureDevopsLink ?? '')
    }, [userDiagnostic]);

    const handleAzureDevopsLinkChange = useCallback(
        (e) => {
            var inputValue = e.target.value;
            setCurrentAzureDevopsLink(inputValue);
        },
        [currentAzureDevopsLink]
    );

    const handleAzureDevopsLinkRemoved = useCallback(
        () => {
            userDiagnosticStore.updateDevopsLink({ userDiagnosticId: userDiagnostic?.id, newLink: null })
        },
        [userDiagnostic]
    );

    const isClosedOrHasLink = useMemo(() => {
        return userDiagnostic.state === UserDiagnosticsState.Closed || userDiagnostic.azureDevopsLink !== null;
    }, [userDiagnostic]);
    const isLinkTicketButtonDisabled = useMemo(() => {
        return isClosedOrHasLink || isHandlingTicket || DevopsLinkShouldBeValid(currentAzureDevopsLink) !== false
    }, [isClosedOrHasLink, userDiagnostic, isHandlingTicket, currentAzureDevopsLink]);
    const isRemoveLinkTicketButtonDisabled = useMemo(() => {
        return userDiagnostic.state === UserDiagnosticsState.Closed || isHandlingTicket
    }, [userDiagnostic, isHandlingTicket]);
    const isLinkTicketInputDisabled = useMemo(() => {
        return isClosedOrHasLink || isHandlingTicket
    }, [isClosedOrHasLink, isHandlingTicket]);

    return (
        <section className='d-flex align-items-center justify-content-center 4rem'>
            <AppInput
                label={t('UserDiagnostics.Tab.devops_integration.InputLabel')}
                name={'azureDevopsLink'}
                value={currentAzureDevopsLink}
                disabled={isLinkTicketInputDisabled}
                onChange={handleAzureDevopsLinkChange}
                validationRules={[
                    DevopsLinkShouldBeValid
                ]}
            />
            <AppSpacer isHorizontal space='4rem' />
            {!isClosedOrHasLink ? (
                <div className='d-flex align-items-end' style={{ gap: '1rem' }}>
                    {isHandlingTicket && <Loader size='small' />}
                    <AppButton
                        variant='primary'
                        onClick={() => userDiagnosticStore.updateDevopsLink({ userDiagnosticId: userDiagnostic?.id, newLink: currentAzureDevopsLink })}
                        disabled={isLinkTicketButtonDisabled}
                        className='h-100 text-nowrap'
                    >
                        {t('UserDiagnostics.Tab.devops_integration.Button.Link').toString()}
                    </AppButton>
                </div>
            ) : (
                <div className='d-flex align-items-end' style={{ gap: '1rem' }}>
                    {isHandlingTicket && <Loader size='small' />}
                    <AppButton
                        variant='danger'
                        onClick={() => handleAzureDevopsLinkRemoved()}
                        disabled={isRemoveLinkTicketButtonDisabled}
                        className='h-100 text-nowrap'
                    >
                        {t('UserDiagnostics.Tab.devops_integration.Button.LinkRemove').toString()}
                    </AppButton>
                </div>
            )}
        </section>
    );
})